import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ApiKeyStatusBadge } from "@/pages/applications/pages/apiKeys/components/ApiKeyStatusBadge";
import { apiKeyTableStyles as styles } from "@/pages/applications/pages/apiKeys/components/apiKeyTableStyles";
import { maskApiKey } from "@/pages/applications/pages/apiKeys/utils/maskApiKey";

type ApiKey = InterludeTypes["ApiKey"];
type SettableApiKeyStatus = InterludeTypes["SettableApiKeyStatus"];

interface ApiKeyRowProps {
  apiKey: ApiKey;
  onRename: (apiKey: ApiKey) => void;
  onRevoke: (apiKey: ApiKey) => void;
  onStatusUpdated: (status: SettableApiKeyStatus) => void;
}

/**
 * One row, owning its own status mutation.
 *
 * The zodios alias hooks take their path params when the hook is created, not
 * when `mutate` is called, so a single page-level hook cannot address whichever
 * row was clicked. Disabling and enabling are the two actions with no modal to
 * hold the selection for them, so the row holds it instead.
 */
export const ApiKeyRow: React.FC<ApiKeyRowProps> = function ApiKeyRow({
  apiKey,
  onRename,
  onRevoke,
  onStatusUpdated,
}) {
  const { t } = useTranslation();

  const updateMutation = interlude.apiKey.useUpdateApiKey(
    { params: { credentialId: apiKey.id } },
    {
      onSuccess: (updated) => {
        /**
         * Report what the server settled on rather than what was asked for. A
         * response carrying `expired` or a status this build does not know is
         * left unreported: the list has already been invalidated and will show
         * it, and there is no honest wording for a state nobody requested.
         */
        if (updated.status.expected && updated.status.value !== "expired") {
          onStatusUpdated(updated.status.value);
        }
      },
    },
  );

  /**
   * `expired` is derived by interlude from the credential's expiry tag and is
   * never settable, so there is no enable action for it: offering one would
   * promise to clear an expiry this API has no way to change. A status this
   * build does not recognize gets neither action for the same reason -- what
   * the opposite of it would be is unknown.
   */
  const settableStatus: SettableApiKeyStatus | null = !apiKey.status.expected
    ? null
    : apiKey.status.value === "active"
      ? "disabled"
      : apiKey.status.value === "disabled"
        ? "active"
        : null;

  const handleToggleStatus = useHandler(() => {
    if (!settableStatus) return;
    // Only `status` is sent. `PatchApiKeyRequestSchema`'s fields are optional
    // rather than nullable, and interlude reads the body with
    // `exclude_unset=True`, so omitting `name` is what leaves it alone.
    updateMutation.mutate({ status: settableStatus });
  });

  const handleRename = useHandler(() => {
    onRename(apiKey);
  });

  const handleRevoke = useHandler(() => {
    onRevoke(apiKey);
  });

  const hasName = apiKey.name.trim().length > 0;

  return (
    <Box>
      <Box sx={styles.row} data-test="api-key-row">
        <Box sx={styles.colName}>
          {hasName ? (
            <Tooltip title={apiKey.name} arrow>
              <Typography variant="body" color={theme.colors.staticFgTitle}>
                {apiKey.name}
              </Typography>
            </Tooltip>
          ) : (
            /**
             * `name` is an empty string on keys issued before naming existed
             * and on keys CS issued through the internal flow, which interlude
             * lists alongside self-service ones. A blank cell would read as a
             * rendering fault, so the row says what is actually true.
             */
            <Typography variant="body" color={theme.colors.staticFgPlaceholder}>
              {t("apiKey.table.unnamed", "Unnamed key")}
            </Typography>
          )}
        </Box>
        <Box sx={styles.colKey}>
          {/*
           * Masked, and with no tooltip or `title` carrying the rest. The list
           * endpoint returns the full plaintext key, so the only thing keeping
           * it out of the page is this call -- an attribute holding the whole
           * value would put it straight back.
           */}
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {maskApiKey(apiKey.key)}
          </Typography>
        </Box>
        <Box sx={styles.colStatus}>
          <ApiKeyStatusBadge status={apiKey.status} />
        </Box>
        <Box sx={styles.colCreatedAt}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {/*
             * `createdAt` is UTC, built by interlude from the Kong timestamp,
             * and dayjs renders it in the reader's own zone -- which is what
             * they need to line a key up against their own records.
             *
             * Deliberately not `Intl.DateTimeFormat`, which AGENTS.md prefers
             * in general: the pages next to this one all print a timestamp as
             * `YYYY-MM-DD HH:mm` (`productFeeds/index.tsx`,
             * `EmailSenderConfigurationTab.tsx`), and a key's age is something
             * the reader compares across those pages. Please leave it.
             */}
            {dayjs(apiKey.createdAt).format("YYYY-MM-DD HH:mm")}
          </Typography>
        </Box>
        <Box sx={styles.colActions}>
          <Tooltip title={t("apiKey.row.rename", "Rename")} arrow>
            <Box
              component="button"
              type="button"
              sx={styles.actionButton}
              data-test="rename-api-key-btn"
              aria-label={t("apiKey.row.rename", "Rename")}
              onClick={handleRename}
            >
              <MotifIcon un-i-motif="edit" />
            </Box>
          </Tooltip>
          {settableStatus ? (
            <Tooltip
              title={
                settableStatus === "disabled"
                  ? t("apiKey.row.disable", "Disable")
                  : t("apiKey.row.enable", "Enable")
              }
              arrow
            >
              {/*
               * The span is what the tooltip holds on to. A disabled button
               * fires no pointer events, so MUI cannot listen on it and logs
               * "You are providing a disabled `button` child to the Tooltip
               * component" -- and this button is disabled on every pending
               * toggle. `CustomFieldTable.renderRestoreButton` wraps its
               * disabled button the same way.
               */}
              <Box component="span">
                <Box
                  component="button"
                  type="button"
                  sx={styles.actionButton}
                  data-test="toggle-api-key-status-btn"
                  aria-label={
                    settableStatus === "disabled"
                      ? t("apiKey.row.disable", "Disable")
                      : t("apiKey.row.enable", "Enable")
                  }
                  disabled={updateMutation.isPending}
                  onClick={handleToggleStatus}
                >
                  <MotifIcon
                    un-i-motif={
                      settableStatus === "disabled"
                        ? "circle_block"
                        : "circle_check"
                    }
                  />
                </Box>
              </Box>
            </Tooltip>
          ) : null}
          <Tooltip title={t("apiKey.row.revoke", "Revoke")} arrow>
            <Box
              component="button"
              type="button"
              sx={styles.dangerActionButton}
              data-test="revoke-api-key-btn"
              aria-label={t("apiKey.row.revoke", "Revoke")}
              onClick={handleRevoke}
            >
              <MotifIcon un-i-motif="bin" />
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={styles.divider} />
    </Box>
  );
};
